import { App as AntdApp, ConfigProvider, theme } from 'antd'
import { useResolvedTheme } from './stores/themeStore'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Spin } from 'antd'
import { useAuthStore } from './stores/authStore'
import { usePermissions } from './hooks/usePermissions'
import type { AdminSection } from './config/permissions'
import { UPCOMING_BUSINESS_MODULES, UPCOMING_MINI_APPS } from './config/upcomingModules'

const LoginPage = lazy(() => import('./pages/login/LoginPage').then((m) => ({ default: m.LoginPage })))
const MfaRecoveryPage = lazy(() => import('./pages/login/MfaRecoveryPage').then((m) => ({ default: m.MfaRecoveryPage })))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const SupplyDashboardPage = lazy(() =>
  import('./pages/supplyDashboard/SupplyDashboardPage').then((m) => ({ default: m.SupplyDashboardPage }))
)
const UsersPage = lazy(() => import('./pages/users/UsersPage').then((m) => ({ default: m.UsersPage })))
const AlgorithmPage = lazy(() =>
  import('./pages/algorithm/AlgorithmPage').then((m) => ({ default: m.AlgorithmPage }))
)
const MessageCenterPage = lazy(() =>
  import('./pages/messageCenter/MessageCenterPage').then((m) => ({ default: m.MessageCenterPage }))
)
const PlatformSnapshotPage = lazy(() =>
  import('./pages/platformSnapshot/PlatformSnapshotPage').then((m) => ({ default: m.PlatformSnapshotPage }))
)
const MonthlyReportPage = lazy(() =>
  import('./pages/monthlyReport/MonthlyReportPage').then((m) => ({ default: m.MonthlyReportPage }))
)
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })))
const VendorsPage = lazy(() => import('./pages/vendors/VendorsPage').then((m) => ({ default: m.VendorsPage })))
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })))
const SupplierCategoriesPage = lazy(() => import('./pages/supplierCategories/SupplierCategoriesPage').then((m) => ({ default: m.SupplierCategoriesPage })))
const SupplierPlansPage = lazy(() => import('./pages/supplierPlans/SupplierPlansPage').then((m) => ({ default: m.SupplierPlansPage })))
const JobsPage = lazy(() => import('./pages/jobs/JobsPage').then((m) => ({ default: m.JobsPage })))
const SuppliersPage = lazy(() => import('./pages/suppliers/SuppliersPage').then((m) => ({ default: m.SuppliersPage })))
const SupplierDetailPage = lazy(() => import('./pages/suppliers/SupplierDetailPage').then((m) => ({ default: m.SupplierDetailPage })))
const SupplierCataloguePage = lazy(() => import('./pages/suppliers/SupplierCataloguePage').then((m) => ({ default: m.SupplierCataloguePage })))
const EnquiriesPage = lazy(() => import('./pages/enquiries/EnquiriesPage').then((m) => ({ default: m.EnquiriesPage })))
const OneMallOverviewPage = lazy(() =>
  import('./pages/onemall/OneMallOverviewPage').then((m) => ({ default: m.OneMallOverviewPage }))
)
const OneMallProductsPage = lazy(() =>
  import('./pages/onemall/OneMallProductsPage').then((m) => ({ default: m.OneMallProductsPage }))
)
const OneMallSellersPage = lazy(() =>
  import('./pages/onemall/OneMallSellersPage').then((m) => ({ default: m.OneMallSellersPage }))
)
const OneMallSellerDetailPage = lazy(() =>
  import('./pages/onemall/OneMallSellerDetailPage').then((m) => ({ default: m.OneMallSellerDetailPage }))
)
const OneMallEnquiriesPage = lazy(() =>
  import('./pages/onemall/OneMallEnquiriesPage').then((m) => ({ default: m.OneMallEnquiriesPage }))
)
const AdminsPage = lazy(() => import('./pages/admins/AdminsPage').then((m) => ({ default: m.AdminsPage })))
const HashtagsPage = lazy(() => import('./pages/hashtags/HashtagsPage').then((m) => ({ default: m.HashtagsPage })))
const CallsPage = lazy(() => import('./pages/rtc/CallsPage').then((m) => ({ default: m.CallsPage })))
const LivePage = lazy(() => import('./pages/rtc/LivePage').then((m) => ({ default: m.LivePage })))
const SyntheticBotsPage = lazy(() => import('./pages/syntheticBots/SyntheticBotsPage').then((m) => ({ default: m.SyntheticBotsPage })))
const SecurityPage = lazy(() => import('./pages/security/SecurityPage').then((m) => ({ default: m.SecurityPage })))
const NotificationCampaignsPage = lazy(() =>
  import('./pages/notificationCampaigns/NotificationCampaignsPage').then((m) => ({ default: m.NotificationCampaignsPage }))
)
const AffiliateCampaignsPage = lazy(() =>
  import('./pages/affiliateCampaigns/AffiliateCampaignsPage').then((m) => ({ default: m.AffiliateCampaignsPage }))
)
const GrowthCampaignsPage = lazy(() =>
  import('./pages/growthCampaigns/GrowthCampaignsPage').then((m) => ({ default: m.GrowthCampaignsPage }))
)
const SuppliersReportPage = lazy(() =>
  import('./pages/monthlyReports/SuppliersReportPage').then((m) => ({ default: m.SuppliersReportPage }))
)
const CreativesReportPage = lazy(() =>
  import('./pages/monthlyReports/CreativesReportPage').then((m) => ({ default: m.CreativesReportPage }))
)
const WalletReportPage = lazy(() =>
  import('./pages/monthlyReports/WalletReportPage').then((m) => ({ default: m.WalletReportPage }))
)
const UsersReportPage = lazy(() =>
  import('./pages/monthlyReports/UsersReportPage').then((m) => ({ default: m.UsersReportPage }))
)
const JobsReportPage = lazy(() =>
  import('./pages/monthlyReports/JobsReportPage').then((m) => ({ default: m.JobsReportPage }))
)
const HashtagsReportPage = lazy(() =>
  import('./pages/monthlyReports/HashtagsReportPage').then((m) => ({ default: m.HashtagsReportPage }))
)
const OneMallReportPage = lazy(() =>
  import('./pages/monthlyReports/OneMallReportPage').then((m) => ({ default: m.OneMallReportPage }))
)
const ComingSoonPage = lazy(() =>
  import('./pages/comingSoon/ComingSoonPage').then((m) => ({ default: m.ComingSoonPage }))
)
const AdBookingsPage = lazy(() =>
  import('./pages/advertising/BookingsPage').then((m) => ({ default: m.BookingsPage }))
)
const AdSlotCalendarPage = lazy(() =>
  import('./pages/advertising/SlotCalendarPage').then((m) => ({ default: m.SlotCalendarPage }))
)
const AdCreativesPage = lazy(() =>
  import('./pages/advertising/CreativesPage').then((m) => ({ default: m.CreativesPage }))
)
const AdCampaignMetricsPage = lazy(() =>
  import('./pages/advertising/CampaignMetricsPage').then((m) => ({ default: m.CampaignMetricsPage }))
)
const AdvertisersPage = lazy(() =>
  import('./pages/advertising/AdvertisersPage').then((m) => ({ default: m.AdvertisersPage }))
)
const AdReportsPage = lazy(() =>
  import('./pages/advertising/AdReportsPage').then((m) => ({ default: m.AdReportsPage }))
)
const WalletsPage = lazy(() => import('./pages/wallets/WalletsPage').then((m) => ({ default: m.WalletsPage })))

const VideoModerationPage = lazy(() =>
  import('./pages/creative/VideoModerationPage').then((m) => ({ default: m.VideoModerationPage }))
)
const CreatorApplicationsPage = lazy(() =>
  import('./pages/creative/CreatorApplicationsPage').then((m) => ({ default: m.CreatorApplicationsPage }))
)
const PipelineHealthPage = lazy(() =>
  import('./pages/creative/PipelineHealthPage').then((m) => ({ default: m.PipelineHealthPage }))
)
const AnalyticsPage = lazy(() =>
  import('./pages/creative/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage }))
)

const CataloguePage = lazy(() =>
  import('./pages/creative/CataloguePage').then((m) => ({ default: m.CataloguePage }))
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <Spin size="large" />
  </div>
)

/** The API refuses whatever a role's permission matrix doesn't allow — keep those
 *  pages out of reach client-side too, so nobody opens a page that only 403s. A
 *  blocked role is sent to the first page it may open (Security as last resort,
 *  which every role can reach), so this can never redirect-loop. */
function RequireSection({ section }: { section: AdminSection }) {
  const role = useAuthStore((s) => s.admin?.role)
  const { level, homePath } = usePermissions()
  if (!role) return <Navigate to="/login" replace />
  return level(section) !== 'none' ? <Outlet /> : <Navigate to={homePath} replace />
}

/**
 * Pages inside an open section that only ADMIN_CONTROL_ALLOWED_EMAILS admins and
 * the Supplier role may open (OneMall Products / Sellers / Enquiries; Todd, 3 Nov
 * 2026). Waits for the permissions answer, so nobody allowed is bounced while it
 * loads; everyone else goes to `fallback`.
 */
function RequireSupplyInsights({ fallback }: { fallback: string }) {
  const { canSeeSupplyInsights, permissionsLoaded } = usePermissions()
  if (!permissionsLoaded) return null
  return canSeeSupplyInsights ? <Outlet /> : <Navigate to={fallback} replace />
}

function HomeRedirect() {
  const role = useAuthStore((s) => s.admin?.role)
  const { homePath } = usePermissions()
  return <Navigate to={role ? homePath : '/login'} replace />
}

export function App() {
  const resolvedTheme = useResolvedTheme()
  const dark = resolvedTheme === 'dark'

  // Our own CSS (shell, glass, placeholders) follows <html data-theme>; antd
  // follows the algorithm below.
  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme
  }, [resolvedTheme])

  return (
    <ConfigProvider
      theme={{
        algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#8648ff',
          borderRadius: 6,
          // Brand body font (Manrope); headings switch to Bricolage Grotesque in
          // styles/global.css.
          fontFamily: 'var(--font-secondary)',
          // The website's dark surfaces, so both apps feel like one product.
          ...(dark && {
            colorBgLayout: '#0c1014',
            colorBgContainer: '#12171c',
            colorBgElevated: '#1a2026',
            colorBorder: '#2f363d',
            colorBorderSecondary: '#22282e',
            colorLink: '#b598ff',
          }),
        },
      }}
    >
      {/* antd's App provider. `App.useApp()` returns inert no-op message and
          modal methods outside it, so feedback on moderation actions would
          silently never appear. */}
      <AntdApp>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/mfa-recovery" element={<MfaRecoveryPage />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<AppLayout />}>
                  <Route index element={<HomeRedirect />} />

                  {/* Account security (2FA) — every admin manages their own, whatever their role */}
                  <Route path="/security" element={<SecurityPage />} />

                  {/* Every other page is gated on its section of the permission matrix
                      (config/permissions.ts; the server's copy via GET /admin/permissions). */}
                  <Route element={<RequireSection section="dashboard" />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                  </Route>
                  <Route element={<RequireSection section="supplyDashboard" />}>
                    <Route path="/supply-dashboard" element={<SupplyDashboardPage />} />
                  </Route>
                  <Route element={<RequireSection section="monthlyReport" />}>
                    <Route path="/monthly-report" element={<MonthlyReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportSuppliers" />}>
                    <Route path="/monthly-reports/suppliers" element={<SuppliersReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportCreatives" />}>
                    <Route path="/monthly-reports/creatives" element={<CreativesReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportWallet" />}>
                    <Route path="/monthly-reports/wallet" element={<WalletReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportUsers" />}>
                    <Route path="/monthly-reports/users" element={<UsersReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportJobs" />}>
                    <Route path="/monthly-reports/jobs" element={<JobsReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportHashtags" />}>
                    <Route path="/monthly-reports/hashtags" element={<HashtagsReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="reportOneMall" />}>
                    <Route path="/monthly-reports/onemall" element={<OneMallReportPage />} />
                  </Route>
                  <Route element={<RequireSection section="users" />}>
                    <Route path="/users" element={<UsersPage />} />
                  </Route>
                  {/* Not RequireSection-wrapped — Message Center doesn't belong to the
                      role-permission matrix. The real gate is server-side on every
                      request (MESSAGE_CENTER_ALLOWED_EMAILS); the page itself checks
                      access on mount and shows a plain denial rather than the search UI
                      for anyone not on that allowlist. */}
                  <Route path="/message-center" element={<MessageCenterPage />} />
                  {/* Same arrangement as Message Center: ALGORITHM_ALLOWED_EMAILS
                      server-side, an access check + 2FA unlock on the page. */}
                  <Route path="/algorithm" element={<AlgorithmPage />} />
                  {/* Platform snapshot report: all three allowlists (Admin Control,
                      Message Center, Algorithm) server-side, an access check on the
                      page and a fresh 2FA code per generation. */}
                  <Route path="/platform-snapshot" element={<PlatformSnapshotPage />} />
                  <Route element={<RequireSection section="reports" />}>
                    <Route path="/reports" element={<ReportsPage />} />
                  </Route>
                  <Route element={<RequireSection section="suppliers" />}>
                    <Route path="/suppliers" element={<SuppliersPage />} />
                    <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
                    <Route path="/suppliers/:id/catalogue" element={<SupplierCataloguePage />} />
                  </Route>
                  <Route element={<RequireSection section="supplierCategories" />}>
                    <Route path="/supplier-categories" element={<SupplierCategoriesPage />} />
                  </Route>
                  <Route element={<RequireSection section="supplierPlans" />}>
                    <Route path="/supplier-plans" element={<SupplierPlansPage />} />
                  </Route>
                  <Route element={<RequireSection section="supplierEnquiries" />}>
                    <Route path="/enquiries" element={<EnquiriesPage />} />
                  </Route>
                  {/* OneMall (Mini-Apps): Shop products, read-only. */}
                  <Route element={<RequireSection section="oneMall" />}>
                    <Route path="/onemall" element={<Navigate to="/onemall/overview" replace />} />
                    <Route path="/onemall/overview" element={<OneMallOverviewPage />} />
                    {/* ADMIN_CONTROL_ALLOWED_EMAILS and the Supplier role (Todd, 2026-10-29 / 3 Nov 2026); the API refuses everyone else. */}
                    <Route element={<RequireSupplyInsights fallback="/onemall/overview" />}>
                      <Route path="/onemall/products" element={<OneMallProductsPage />} />
                      <Route path="/onemall/sellers" element={<OneMallSellersPage />} />
                      <Route path="/onemall/sellers/:userId" element={<OneMallSellerDetailPage />} />
                      <Route path="/onemall/enquiries" element={<OneMallEnquiriesPage />} />
                    </Route>
                  </Route>
                  <Route element={<RequireSection section="creativeCatalogue" />}>
                    <Route path="/creative/manage" element={<CataloguePage />} />
                    <Route path="/creative/series" element={<CataloguePage />} />
                  </Route>
                  <Route element={<RequireSection section="creativeModeration" />}>
                    <Route path="/creative/videos" element={<VideoModerationPage />} />
                  </Route>
                  <Route element={<RequireSection section="creativeCreators" />}>
                    <Route path="/creative/creators" element={<CreatorApplicationsPage />} />
                  </Route>
                  <Route element={<RequireSection section="creativePipeline" />}>
                    <Route path="/creative/pipeline" element={<PipelineHealthPage />} />
                  </Route>
                  <Route element={<RequireSection section="creativeAnalytics" />}>
                    <Route path="/creative/analytics" element={<AnalyticsPage />} />
                  </Route>
                  <Route element={<RequireSection section="vendors" />}>
                    <Route path="/vendors" element={<VendorsPage />} />
                  </Route>
                  <Route element={<RequireSection section="appSettings" />}>
                    <Route path="/settings" element={<SettingsPage />} />
                  </Route>
                  <Route element={<RequireSection section="onboarding" />}>
                    <Route path="/onboarding" element={<OnboardingPage />} />
                  </Route>
                  <Route element={<RequireSection section="jobs" />}>
                    <Route path="/jobs" element={<JobsPage />} />
                  </Route>
                  <Route element={<RequireSection section="hashtags" />}>
                    <Route path="/hashtags" element={<HashtagsPage />} />
                  </Route>
                  <Route element={<RequireSection section="calls" />}>
                    <Route path="/calls" element={<CallsPage />} />
                  </Route>
                  <Route element={<RequireSection section="live" />}>
                    <Route path="/live" element={<LivePage />} />
                  </Route>
                  <Route element={<RequireSection section="admins" />}>
                    <Route path="/admins" element={<AdminsPage />} />
                  </Route>
                  <Route element={<RequireSection section="syntheticBots" />}>
                    <Route path="/synthetic-bots" element={<SyntheticBotsPage />} />
                  </Route>
                  {/* Marketing group (Sidebar.tsx's `marketing-group`) */}
                  <Route element={<RequireSection section="notificationCampaigns" />}>
                    <Route path="/notification-campaigns" element={<NotificationCampaignsPage />} />
                  </Route>
                  <Route element={<RequireSection section="affiliateCodes" />}>
                    <Route path="/affiliate-campaigns" element={<AffiliateCampaignsPage />} />
                  </Route>
                  <Route element={<RequireSection section="growthCampaigns" />}>
                    <Route path="/growth-campaigns" element={<GrowthCampaignsPage />} />
                  </Route>
                  <Route element={<RequireSection section="wallets" />}>
                    <Route path="/wallets" element={<WalletsPage />} />
                  </Route>
                  {/* High Ticket Ad Space (Sidebar.tsx's `advertising-group`). Approvals-only
                      actions inside these pages are gated on `advertisingApprovals`. */}
                  <Route element={<RequireSection section="advertising" />}>
                    <Route path="/advertising" element={<Navigate to="/advertising/bookings" replace />} />
                    <Route path="/advertising/bookings" element={<AdBookingsPage />} />
                    <Route path="/advertising/calendar" element={<AdSlotCalendarPage />} />
                    <Route path="/advertising/creatives" element={<AdCreativesPage />} />
                    <Route path="/advertising/metrics" element={<AdCampaignMetricsPage />} />
                    <Route path="/advertising/advertisers" element={<AdvertisersPage />} />
                    <Route path="/advertising/reports" element={<AdReportsPage />} />
                  </Route>
                  {/* Placeholders in the Business Modules and Mini-Apps groups
                      (config/upcomingModules.ts): one "Coming soon" page each,
                      Super Admin and Admin only. */}
                  <Route element={<RequireSection section="upcomingModules" />}>
                    {[...UPCOMING_BUSINESS_MODULES, ...UPCOMING_MINI_APPS].map((m) => (
                      <Route key={m.path} path={m.path} element={<ComingSoonPage name={m.name} />} />
                    ))}
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  )
}
